import React, { useEffect, useState, useCallback, CSSProperties, useMemo } from 'react';
import { Column, useTable, useFilters, Row, useSortBy, Filters, SortingRule, useAsyncDebounce, TableState } from 'react-table';
import { ArrowDownOutlined } from '@ant-design/icons';
import { Empty } from 'antd';
import { ITablePropertyExtensions, IColumnPropertyExtensions } from 'types/components/react_table/IReactTablePropertyExtensions';
import { defaultTextContainsFilter } from 'functions/Filters';
import { cnTblHeadRow, cnTblHeadRowUnderlineLast, cnTblHeadCell, cnTblHeadCellWired, cnTblBody, cnTblFoot, cnTblFootRow, cnTblFootCell, cnTblNoDataMessage, cnTbl, cnTblHead, cnTblBodyRow, cnTblBodyRowAltColor, cnTblBodyCell, cnTblBodyCellWired, cnTblBodyRowClickable, cnTblHeadCellSortable } from './TableStyleConstants';


// data-table alternate-color-rows wired underline-heading
const headRowClassName = `${cnTblHeadRow} ${cnTblHeadRowUnderlineLast}`;
const headCellClassName = `${cnTblHeadCell} ${cnTblHeadCellWired}`;
const bodyRowClassName = `${cnTblBodyRow} ${cnTblBodyRowAltColor}`; //${cnTblBodyRowUnderlineLast}
const bodyCellClassName = `${cnTblBodyCell} ${cnTblBodyCellWired}`;

interface IProps
{
	columns: Column<Object>[],
	data: Object[],
	initialFilters?: Filters<Object>,
	initialSortBy?: SortingRule<Object>[],
	onFiltersChanged: (filters: Filters<Object>) => void,
	onSortingChanged: (sortingRules: SortingRule<Object>[]) => void,
	rowTooltip?: string | undefined,
	onRowClick?: (row: Row<Object>) => void,
	pageSize?: number | undefined,
	/** Possible footer that was passed */
	footer?: React.ReactNode | undefined,
	noDataMessage?: string | undefined,
	propertyExtensions?: ITablePropertyExtensions
}


/** Default table with sorting and filtering which should be controlled by backend data */
function DefaultDataTable(props: IProps)
{
	const [componentState, setComponentState] = useState({
		hasInitialized: false
	});

	// console.trace(`rerender ${DefaultDataTable.name}`);
	const {
		columns,
		data = [],
		onFiltersChanged,
		onSortingChanged
	} = props;

	// const [componentState, setComponentState] = useState({ hasInitialized: false });

	const filterTypes: Record<string, any> = React.useMemo(
		() => ({
			// Override the default text filter to use
			text: defaultTextContainsFilter,
		}),
		[]
	)

	const initialState = useMemo(() =>
	{
		let state: Partial<TableState<Object>> | undefined = {};
		if (props.initialFilters) state.filters = props.initialFilters;
		if (props.initialSortBy) state.sortBy = props.initialSortBy;
		return state;
	}, [props.initialFilters, props.initialSortBy]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		state,
	} = useTable(
		{
			initialState: initialState,
			columns,
			data,
			filterTypes,
			manualFilters: true,
			defaultCanFilter: true,
			manualSortBy: true
		},
		useFilters,
		useSortBy
	);

	// Hacks from their own library... -> https://media1.tenor.com/images/c1bbaf1dd4705bd3dd448f20d2ba056e/tenor.gif?itemid=5105574
	// https://github.com/tannerlinsley/react-table/blob/master/docs/faq.md#how-do-i-stop-my-table-state-from-automatically-resetting-when-my-data-changes
	const onFiltersChangedDebounced = useAsyncDebounce(() => onFiltersChanged(state.filters), 100);
	const onSortByChangedDebounced = useAsyncDebounce(() => onSortingChanged(state.sortBy), 100);

	// Callbacks
	useEffect(() =>
	{
		if (componentState.hasInitialized) {
			onFiltersChangedDebounced();
		} else {
			setComponentState({
				...componentState, hasInitialized: true
			})
		}
	}, [state.filters, onFiltersChangedDebounced, componentState]); //ToCheckPieter

	useEffect(() =>
	{
		if (componentState.hasInitialized) {
			onSortByChangedDebounced();
		} else {
			setComponentState({
				...componentState, hasInitialized: true
			})
		}
	}, [state.sortBy, onSortByChangedDebounced, componentState]);

	const rowClickCallback = useCallback((row: Row<Object>) =>
	{
		if (props.onRowClick) {
			props.onRowClick(row);
		}
	}, [props]);

	/** 
	 * Renders the default data rows and additional empty rows if the pagesize 
	 * property is higher than the amount of data rows on the page.
	*/
	function getRows(): JSX.Element[]
	{
		const pageSize = props.pageSize ? props.pageSize : rows.length;
		// const columnCount = columns.length;

		const rowElements: JSX.Element[] = [];

		for (let i = 0; i < pageSize; i++) {
			if (i < rows.length) {
				const row: Row<Object> = rows[i];
				prepareRow(row);

				const rowElement = (
					<tr 
						// key={i} 
						{...row.getRowProps()}
						onClick={() => rowClickCallback(row)}
						className={`${bodyRowClassName} ${cnTblBodyRowClickable}`}
						title={props.rowTooltip}
					>
						{row.cells.map((cell, index) =>
						{
							let cellStylings: React.CSSProperties = {};
							if (cell.column.minWidth) cellStylings.minWidth = cell.column.minWidth;
							if (cell.column.maxWidth) cellStylings.maxWidth = cell.column.maxWidth;
							if (cell.column.width) cellStylings.width = cell.column.width;

							let columnStyleExtension = getColumnStyleExtensions(props.propertyExtensions, cell.column.id);
							if (columnStyleExtension) {
								cellStylings = { ...cellStylings, ...columnStyleExtension.style };
								// console.log('cellStylings', cellStylings);
							}

							return (
								<td 
									// key={index}
									{...cell.getCellProps()}
									style={cellStylings}
									className={bodyCellClassName}
								>
									{cell.render('Cell')}
								</td>
							)
						})}
					</tr>
				)

				rowElements.push(rowElement);
			} else {
				const cellElements: JSX.Element[] = [];
				for (let j = 0; j < columns.length; j++) {
					cellElements.push(<td key={j} className={bodyCellClassName}></td>);
				}

				const row = <tr key={i} className={bodyRowClassName}>{cellElements}</tr>
				rowElements.push(row);
			}
		}

		return rowElements;
	}

	return (
		<>
			<table {...getTableProps()} className={cnTbl}>
				<thead className={cnTblHead}>
					{headerGroups.map((headerGroup, hgIndex) => (
						<React.Fragment key={hgIndex}>
							<tr
								{...headerGroup.getHeaderGroupProps()}
								className={headRowClassName}
							>
								{headerGroup.headers.map((column, index) =>
								{
									// console.log(column);
									let className = '';
									if (column.isSortedDesc === true) {
										className = 'visible up';
									} else if (column.isSortedDesc === false) {
										className = 'visible';
									}

									let styles: CSSProperties = {};
									let columnStyleExtension = getColumnStyleExtensions(props.propertyExtensions, column.id);
									if (columnStyleExtension) {
										styles = columnStyleExtension.style;
									}

									// console.log(column);

									let cellClassName = headCellClassName;
									if (column.canSort) {
										cellClassName += ' ' + cnTblHeadCellSortable;
									}

									return (
										<th
											// key={index}
											className={cellClassName}
											{...column.getHeaderProps(column.getSortByToggleProps())} title=''
											style={styles}
										>
											<>
												{column.render('Header')}
												{column.canSort && <ArrowDownOutlined className={className} />}
											</>
										</th>
									)
								})}
							</tr>
							<tr className={headRowClassName}>
								{headerGroup.headers.map((column, index) =>
								{
									// console.log(column);
									return (
										<th key={index} className={headCellClassName}>
											{/* Render the columns filter UI */}
											{column.filter ? column.render('Filter') : ''}
										</th>
									)
								})}
							</tr>
						</React.Fragment>
					))}
				</thead>
				<tbody {...getTableBodyProps()} className={cnTblBody}>
					{getRows()}
				</tbody>
				{props.footer &&
					<tfoot className={cnTblFoot}>
						<tr className={cnTblFootRow}>
							<td className={cnTblFootCell} colSpan={columns.length}>
								{props.footer}
							</td>
						</tr>
					</tfoot>
				}
			</table>
			{rows.length === 0 && props.noDataMessage &&
				<div className={cnTblNoDataMessage}>
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={props.noDataMessage}
						className="pos-abs-center"
						style={{ margin: 0 }}
					/>
				</div>
			}
		</>
	);
};

function getColumnStyleExtensions(tablePropExtensions: ITablePropertyExtensions | undefined, accessor: string): IColumnPropertyExtensions | undefined
{
	if (tablePropExtensions) {
		return tablePropExtensions.columnExtensions.find(ce => ce.accessor === accessor);
	} else {
		return undefined;
	}
}

export default DefaultDataTable;